import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import EventBus from 'eventing-bus';
import { verify2FA, resend2FA } from '../../store/Auth';
import './style.css';

const OTPVerification = ({ userId, email, onBack, history }) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(300);
  const inputRefs = useRef([]);
  const timerIntervalRef = useRef(null);

  const startTimer = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setTimer(300);
    timerIntervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 0) {
          clearInterval(timerIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (inputRefs.current[0]) inputRefs.current[0].focus();
    startTimer();
    return () => clearInterval(timerIntervalRef.current);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newOtp.every(digit => digit !== '')) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
      
      // Focus last filled input or last input
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
      
      // Auto-submit if 6 digits
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = (otpCode = otp.join('')) => {
    if (otpCode.length !== 6) {
      EventBus.publish('error', 'Please enter all 6 digits');
      return;
    }

    if (timer <= 0) {
      EventBus.publish('error', 'Code has expired. Please login again.');
      return;
    }

    setIsLoading(true);
    dispatch(verify2FA({
      userId,
      otpCode,
      history,
      successCallback: () => {
        setIsLoading(false);
      },
      failCallback: () => {
        setIsLoading(false);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    }));
  };

  const handleResend = () => {
    setIsResending(true);
    dispatch(resend2FA({
      userId,
      successCallback: () => {
        setIsResending(false);
        setOtp(['', '', '', '', '', '']);
        startTimer();
        inputRefs.current[0]?.focus();
      },
      failCallback: () => setIsResending(false),
    }));
  };

  return (
    <div className="otp-verification">
      <h3>Two-Factor Authentication</h3>
      <p className="otp-description">
        Enter the 6-digit code sent to <strong>{email}</strong>
      </p>
      
      <div className="otp-inputs">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            maxLength="1"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isLoading}
            className="otp-input"
            autoComplete="off"
          />
        ))}
      </div>

      <div className="otp-timer">
        {timer > 0 ? (
          <span>Code expires in: <strong>{formatTime(timer)}</strong></span>
        ) : (
          <span className="expired">Code has expired</span>
        )}
      </div>

      <div className="otp-actions">
        <button 
          onClick={() => handleVerify()} 
          disabled={isLoading || otp.some(d => !d)}
          className="btn-verify"
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>
        
        <button
          onClick={handleResend}
          className="btn-resend"
          disabled={isLoading || isResending}
        >
          {isResending ? 'Sending...' : 'Resend Code'}
        </button>
      </div>

      <button 
        onClick={onBack}
        className="btn-back"
        disabled={isLoading}
      >
        ← Back to Login
      </button>
    </div>
  );
};

export default OTPVerification;